import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { crewService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import CrewForm from "../components/CrewForm";
import type { CrewMember } from "../types/database";

export default function CrewEdit() {
  const { orgId, id: crewId } = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();

  // Redirect to crew list if no ID is provided
  useEffect(() => {
    if (!crewId && orgId) {
      console.log("No crew ID provided, redirecting to crew list");
      navigate(`/org/${orgId}/crew`);
    }
  }, [crewId, orgId, navigate]);

  // Fetch crew member data
  const {
    data: crewMember,
    isLoading,
    error,
    refetch,
  } = useQuery<CrewMember>({
    queryKey: ["crew", crewId],
    queryFn: async () => {
      try {
        if (!crewId) {
          throw new Error("Crew member ID is missing");
        }

        if (!currentOrganization?.id) {
          throw new Error("Organization ID is missing");
        }

        return await crewService.getCrewMember(crewId, currentOrganization.id);
      } catch (err) {
        console.error("Error fetching crew member for edit:", err);
        throw err;
      }
    },
    enabled: !!crewId && !!currentOrganization?.id,
    retry: 1, // Only retry once to avoid excessive requests on permanent errors
  });

  // Handle successful form submission
  const handleSuccess = (crewMember: CrewMember) => {
    navigate(`/org/${orgId}/crew/${crewMember.id}`);
  };

  // If no crew ID, show a message and redirect
  if (!crewId) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-gray-500 mb-2 text-lg font-medium">
          No crew member selected
        </div>
        <div className="text-sm text-gray-500 mb-4">
          Redirecting to crew list...
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !crewMember) {
    const errorDetails =
      error instanceof Error ? error.message : JSON.stringify(error, null, 2);

    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-red-500 mb-2 text-lg font-medium">
          Error loading crew member details
        </div>
        <div className="text-sm text-gray-500 mb-4 max-w-2xl overflow-auto">
          {errorDetails === "null" ? (
            <p>
              Unable to retrieve crew member data. This may be due to missing
              permissions or the crew member doesn't exist.
            </p>
          ) : (
            <pre className="whitespace-pre-wrap bg-gray-100 p-4 rounded-md">
              {errorDetails}
            </pre>
          )}
        </div>
        <div className="flex space-x-4">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate(`/org/${orgId}/crew`)}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"
          >
            Return to Crew List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pb-5 border-b border-gray-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Edit Crew Member: {crewMember.first_name} {crewMember.last_name}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Update the crew member's information
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <CrewForm
          crewMember={crewMember}
          onSuccess={handleSuccess}
          orgId={orgId!}
        />
      </div>
    </div>
  );
}
